import { Component, OnChanges, OnInit, Renderer2 } from "@angular/core";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import * as FileSaver from "file-saver";
import { ExternalService } from "../../../../core/services/external.service";
import { SharedService } from "../../../../core/services/shared.service";
import { GetAllSpecialsDto } from "../../../../core/models/specials/getAllSpecials";
import { CommonModule } from "@angular/common";
import {
  TableFilterEvent,
  TableModule,
  TableLazyLoadEvent,
} from "primeng/table";
import { FormsModule } from "@angular/forms";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { FilterMetadata } from "primeng/api";
import { GenericRequestDto } from "../../../../core/models/common/genericRequestDto";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";
import { GenericFilterRequestDto } from "../../../../core/models/common/genericFilterRequestDto";
import { GenericSortRequestDto } from "../../../../core/models/common/genericSortRequestDto";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { VendorSummaryComponent } from "../../../vendors/components/vendor-summary/vendor-summary.component";

@UntilDestroy()
@Component({
  selector: "jni-specials-listing",
  templateUrl: "./specials-listing.component.html",
  styleUrls: ["./specials-listing.component.scss"],
  imports: [
    CommonModule,
    TableModule,
    FormsModule,
    CardModule,
    ButtonModule,
    SpinnerComponent,
    ModalStackerComponent,
    VendorSummaryComponent,
  ],
})
export class SpecialsListingComponent implements OnInit, OnChanges {
  vendorAccountNumber = "";
  vendorDisplayModal = false;
  vendorListingModalStatus = false;

  constructor(
    private _router: Router,
    private _externalService: ExternalService,
    private _sharedService: SharedService,
    private renderer: Renderer2
  ) {}

  specialsListings: GetAllSpecialsDto[] = [];
  filteredDataSpecialsList: GetAllSpecialsDto[] = [];
  showLoader = false;
  totalRecords = 0;
  defaultTake = 50;
  lazyLoadEvent!: TableLazyLoadEvent;
  filterTextChanged: Subject<Event | string | null> = new Subject<
    Event | string | null
  >();
  filterDebounceTime = 1000;

  ngOnInit(): void {
    this.clearAndSetSimpleOrderSearchResponseObj();
    this._sharedService.setOrderSearchValue("");
    this._sharedService.setOrderSearchPlaceholderValue("Search");
  }

  ngOnChanges() {
    if (this.vendorAccountNumber) {
      this.vendorListingModalStatus = true;
    }
  }
  clearAndSetSimpleOrderSearchResponseObj() {
    const orderSearch: string[] = [];
    this._sharedService.setSimpleOrderSearchResponseObject(orderSearch);
  }

  getAllSpecialsListingData(event: TableLazyLoadEvent) {
    this.showLoader = true;
    this.lazyLoadEvent = event;
    const eventFilterArray = Object.keys(event.filters ?? {});

    const filter: GenericFilterRequestDto[] = eventFilterArray.map(x => {
      const tableFilter = event?.filters?.[x] as FilterMetadata;
      const data = {
        columnName: x ?? "",
        matchMode: tableFilter.matchMode ?? "",
        tableName: "",
        value: tableFilter.value ?? "",
      } as GenericFilterRequestDto;

      return data;
    });

    const sortArray: GenericSortRequestDto[] =
      event.multiSortMeta?.map(x => {
        return {
          ...x,
        } as GenericSortRequestDto;
      }) ?? [];

    filter.forEach(x => {
      if (x.columnName == "vendorAccountNumber") {
        x.columnName = "vendorAccountNumber";
        x.tableName = "Specials";
      }

      if (x.columnName == "eligibleProduct") {
        x.columnName = "eligibleProduct";
        x.tableName = "Specials";
      }

      if (x.columnName == "pricing") {
        x.columnName = "pricing";
        x.tableName = "Specials";
      }

      if (x.columnName == "vendorContact") {
        x.columnName = "vendorContact";
        x.tableName = "Specials";
      }
    });

    const request: GenericRequestDto = {
      skip: event.first ?? 0,
      take: event.rows ?? this.defaultTake,
      filters: filter,
      sort: sortArray,
    };

    this._externalService
      .getAllSpecials(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res: GenericResponseDto<GetAllSpecialsDto[]>) => {
          this.specialsListings = res.data;
          this.filteredDataSpecialsList = this.specialsListings;
          this.showLoader = false;
          this.totalRecords = res.totalRecords ?? 0;
        },
      });
  }

  exportExcel() {
    this.showLoader = true;
    this.exportExcelForFilterData(this.filteredDataSpecialsList);
    this.showLoader = false;
  }

  exportExcelForFilterData(filterSpecialsList: GetAllSpecialsDto[]) {
    const object = filterSpecialsList.map(datum => {
      return {
        ["Vendor Account Number"]: datum.vendorAccountNumber,
        ["Vendor Name"]: datum.vendorName,
        ["Eligible Product"]: datum.eligibleProduct,
        ["Description"]: datum.description,
        ["Pricing"]: datum.pricing,
        ["Effective Date"]: datum.effectiveDate
          ? new Date(datum.effectiveDate).toLocaleDateString()
          : "",
        ["Expiration Date"]: datum.expirationDate
          ? new Date(datum.expirationDate).toLocaleDateString()
          : "",
        ["Vendor Contact"]: datum.vendorContact,
      };
    });

    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(object);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer: ArrayBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.saveAsExcelFile(excelBuffer, "SpecialsList");
    });
  }

  saveAsExcelFile(buffer: ArrayBuffer, fileName: string): void {
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  onFilter(event: TableFilterEvent) {
    if (event?.filters && event?.filteredValue?.length !== 0) {
      this.filteredDataSpecialsList = event.filteredValue;
    } else {
      this.filteredDataSpecialsList = this.specialsListings;
    }
  }

  onFilterInput($event: Event | string | null) {
    this.filterTextChanged
      .pipe(debounceTime(this.filterDebounceTime), distinctUntilChanged())
      .subscribe(() => {
        this.getAllSpecialsListingData(this.lazyLoadEvent);
      });
    this.filterTextChanged.next($event);
  }

  formatDate(date: Date | undefined): string {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  }

  getCurrentVendor(accountnumber: string) {
    if (accountnumber) {
      this.vendorAccountNumber = accountnumber;
      this.vendorDisplayModal = true;
    }
  }

  addScollHiddenClass() {
    if (this.vendorListingModalStatus) {
      this.renderer.addClass(document.body, "p-overflow-hidden");
    } else {
      this.renderer.removeClass(document.body, "p-overflow-hidden");
    }
  }

  closeModal() {
    this.vendorDisplayModal = false;
  }
}
